import { useEffect, useState } from 'react'
import { NO_SKINS, SKIN_CATALOG_PATH } from '@sh/shared'
import type { SkinCatalog } from '@sh/shared'
import { loadSkin } from './skin.js'

/**
 * Fetched once per tab and shared. Which skins exist is a property of the
 * deployment, not of a lobby, so it cannot change under a running game and
 * every screen that asks gets the same answer without a second request.
 */
let pending: Promise<SkinCatalog> | null = null

export function loadSkinCatalog(): Promise<SkinCatalog> {
  pending ??= fetch(SKIN_CATALOG_PATH)
    .then((res) => (res.ok ? res.json() : NO_SKINS))
    .then(parse)
    .then((catalog) => {
      /*
       * The skin a new lobby starts on is the one the landing page wears and
       * the one most tables will play, and finding out which it is costs a
       * second round trip after this one. Started here, that trip and the
       * artwork behind it overlap with somebody typing their name, rather
       * than with the first screen that needs a picture.
       */
      if (catalog.skins.length > 0) void loadSkin(catalog.default)
      return catalog
    })
    .catch(() => NO_SKINS)
  return pending
}

/**
 * Until the server answers - and for good if it never does, which is what a
 * Storybook build or a client served without its API looks like - the answer
 * is "no skins". A table then plays on Minimal and the lobby offers no picker,
 * rather than briefly showing a choice it may not be able to honour.
 */
export function useSkinCatalog(): SkinCatalog {
  const [catalog, setCatalog] = useState<SkinCatalog>(NO_SKINS)

  useEffect(() => {
    let cancelled = false
    void loadSkinCatalog().then((next) => {
      if (!cancelled) setCatalog(next)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return catalog
}

function parse(value: unknown): SkinCatalog {
  const raw = value as Partial<SkinCatalog> | null
  if (!raw || !Array.isArray(raw.skins) || typeof raw.default !== 'string') return NO_SKINS
  return {
    enabled: raw.enabled === true,
    default: raw.default,
    skins: raw.skins.filter((s) => typeof s?.id === 'string' && typeof s?.name === 'string'),
  }
}
